import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import type { BankOutcome as Outcome } from '~/state/bank-outcome'
import { CONSENT_PARAM, FAILURE_PARAM } from '@/api/bank-consent'
import { attributionId, bankOutcomeOf } from '~/state/bank-outcome'
import type { PendingConsent, SourcesResponse } from '~/state/sources'
import { useSources } from '~/state/sources'

/**
 * What the bank just did, said on the page it sends people back to.
 *
 * The callback ends every path with an answer in the query string, and for a
 * while nothing read it: an approval that failed and one that worked both
 * landed on a Sources page that said "No bank is connected", with no way for
 * the person or anybody helping them to tell which had happened.
 */
export function BankOutcome(): ReactElement | null {
  // Taken once, before the effect below takes it out of the address bar.
  const [outcome] = useState<Outcome | undefined>(() => bankOutcomeOf(window.location.search))
  const sources = useSources()
  const pending = outcome?.kind === 'granted' ? pendingIn(sources.data, outcome.reference) : undefined

  useEffect(() => {
    if (outcome !== undefined) forget()
  }, [outcome])

  useEffect(() => {
    if (pending === undefined) return
    // The approval cost a MitID, and the accounts it produced are worth nothing
    // until somebody says whose they are, so the page arrives at the question
    // rather than leaving it to be scrolled for.
    const form = document.getElementById(attributionId(pending.reference))
    if (form !== null && typeof form.scrollIntoView === 'function') {
      form.scrollIntoView({ block: 'start' })
    }
  }, [pending])

  if (outcome === undefined) return null
  // A consent is confirmed once the sources it belongs to have answered.
  // Confirming before that means saying nothing is waiting and then, a moment
  // later, that two accounts are — two different answers to the one question
  // the reader came back with.
  if (outcome.kind === 'granted' && sources.isPending) return null

  return (
    <p
      className={`nw-flag ${outcome.kind === 'granted' ? 'nw-flag--granted' : 'nw-flag--missing'}`}
      role="status"
      data-outcome={outcome.kind === 'granted' ? 'granted' : outcome.failure}
    >
      <span className="nw-flag-icon" aria-hidden="true">
        {outcome.kind === 'granted' ? '✓' : '✕'}
      </span>
      <span>{outcome.kind === 'granted' ? granted(pending) : outcome.message}</span>
    </p>
  )
}

/**
 * Said once and then taken out of the address bar. A refresh would otherwise
 * re-announce the result of an attempt that is over, and a link somebody copied
 * off this page would carry their consent reference to whoever they sent it to.
 */
function forget(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete(CONSENT_PARAM)
  url.searchParams.delete(FAILURE_PARAM)
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

/**
 * Confirmed whether or not the consent is still waiting. One that is not is one
 * already attributed — a reload of this page, usually — and "your approval came
 * through" stays true, where silence would read as the failure it is not.
 */
function granted(pending: PendingConsent | undefined): string {
  if (pending === undefined) {
    return 'Your bank approved the connection. Nothing more is waiting to be answered here.'
  }
  const accounts = pending.accounts.length
  return `${pending.bank} approved the connection and returned ${accounts} ${
    accounts === 1 ? 'account' : 'accounts'
  }. Say whose money each one holds, below, and it starts counting.`
}

function pendingIn(data: SourcesResponse | undefined, reference: string): PendingConsent | undefined {
  for (const source of data?.sources ?? []) {
    if (source.kind !== 'consent') continue
    const found = source.pending.find((consent) => consent.reference === reference)
    if (found !== undefined) return found
  }
  return undefined
}
